import { getIn } from "formik";
import React from "react";
import Button from "@material-ui/core/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import {
  ALL_VALUE_SOURCES,
  FieldValueType,
  ValueSourceDisplayNameMap,
} from "containers/Settings/RuleBooks/constants";
import { ValueSource } from "constants/ruleBooks";
import {
  getDataTypeByPath,
  normalisePathParts,
} from "containers/Settings/RuleBooks/Schema/schema";
import { useRuleFieldSchema } from "containers/Settings/RuleBooks/Schema/schemaContext";
import { useFieldState } from "hooks";

function getValueSources(
  showAggregations: boolean,
  includeGlobalBusinesses: boolean,
  showParentDataSource: boolean,
  showLedgerEntrySource: boolean,
) {
  let valueSources;
  if (showAggregations && includeGlobalBusinesses) {
    valueSources = ALL_VALUE_SOURCES;
  } else if (showAggregations && !includeGlobalBusinesses) {
    valueSources = ALL_VALUE_SOURCES.filter(
      valuesSource => valuesSource !== ValueSource.GLOBAL_BUSINESS,
    );
  } else if (includeGlobalBusinesses && !showAggregations) {
    valueSources = [
      ValueSource.FIELD,
      ValueSource.CONSTANT,
      ValueSource.GLOBAL_BUSINESS,
    ];
  } else {
    valueSources = [ValueSource.FIELD, ValueSource.CONSTANT];
  }

  if (!showParentDataSource) {
    valueSources = valueSources.filter(
      source => source !== ValueSource.RULESET_FIELD,
    );
  }

  if (!showLedgerEntrySource) {
    valueSources = valueSources.filter(
      source => source !== ValueSource.DEPENDENCY_VALUE,
    );
  }

  return valueSources;
}

interface DataSourcePickerProps {
  dataTour?: string;
  includeGlobalBusinesses?: boolean;
  namespace: string;
  onAfterChanged?: (dataSource: any) => void;
  parentInputPath: string | null;
  fieldPath: string;
  showAggregations?: boolean;
  showPrerequisiteRuleSource?: boolean;
  readOnly?: boolean;
}

function shouldShowRuleSetSource(
  parentInputPath: string | null,
  fullInputPath: string,
): boolean {
  // Show the RuleSet source when the rule does not operate on the same dataset
  // as the parent rule.
  return parentInputPath && fullInputPath !== parentInputPath;
}

export function DataSourcePicker(props: DataSourcePickerProps) {
  const {
    dataTour,
    includeGlobalBusinesses = false,
    namespace: ns,
    onAfterChanged,
    fieldPath,
    parentInputPath = null,
    showAggregations,
    showPrerequisiteRuleSource = false,
    readOnly,
  } = props;

  const [value, setValue] = useFieldState(ns);
  const source = getIn(value, "source");
  const schema = useRuleFieldSchema();

  const fullPath = normalisePathParts([parentInputPath, fieldPath]);

  const showParentDataSource = shouldShowRuleSetSource(
    parentInputPath,
    fullPath,
  );

  const parentDataType = getDataTypeByPath(schema, parentInputPath) || schema;

  const dataType = getDataTypeByPath(schema, fullPath);

  function onChangeSource(newSource: ValueInObj<typeof ValueSource>) {
    const updatedField = {
      ...value,
      criteria_output_index: "",
      source: newSource,
      valueType:
        {
          // When has selected Global Business as the data source, the value type will be a business.
          [ValueSource.GLOBAL_BUSINESS]: FieldValueType.BUSINESS,
          // When the user has selected "the number of" as their data source, it will always be an integer.
          [ValueSource.COUNT]: FieldValueType.INTEGER,
        }[newSource] ||
        // All other data source will fill out their own value type as part of their inner workings.
        null,
    };
    setValue(updatedField);
    if (typeof onAfterChanged === "function") {
      onAfterChanged(updatedField);
    }
  }

  const valueSources = getValueSources(
    showAggregations,
    includeGlobalBusinesses,
    showParentDataSource,
    showPrerequisiteRuleSource,
  );

  const [anchorEl, setAnchorEl] = React.useState(null);

  function handleClose() {
    setAnchorEl(null);
  }

  function handleMenuItemClick(valueSource) {
    handleClose();
    onChangeSource(valueSource);
  }

  function handleClickListItem(event) {
    setAnchorEl(event.currentTarget);
  }

  const dataTypeName = dataType
    ? `The ${dataType.possessive}`
    : ValueSourceDisplayNameMap[ValueSource.FIELD];

  const parentDataTypeName = parentDataType
    ? `The ${parentDataType.possessive}`
    : ValueSourceDisplayNameMap[ValueSource.RULESET_FIELD];

  const sourceNameOverrideMap = {
    [ValueSource.FIELD]: dataTypeName,
    [ValueSource.RULESET_FIELD]: parentDataTypeName,
    [ValueSource.DEPENDENCY_VALUE]: "The Ledger Entry's",
  };

  return (
    <>
      <Button
        onClick={handleClickListItem}
        endIcon={!readOnly ? <FontAwesomeIcon icon={faChevronDown} /> : null}
        variant="text"
        data-tour={`${dataTour}DataSourcePicker`}
        disabled={readOnly}
      >
        {sourceNameOverrideMap[value.source] ||
          ValueSourceDisplayNameMap[value.source]}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {valueSources.map(valueSource => (
          <MenuItem
            value={valueSource}
            key={valueSource}
            selected={source === valueSource}
            onClick={() => handleMenuItemClick(valueSource)}
          >
            {sourceNameOverrideMap[valueSource] ||
              ValueSourceDisplayNameMap[valueSource]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
